import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { RefreshCw, TrendingUp, Clock, AlertTriangle, CheckCircle2, BarChart3, Activity } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function AnalyticsView() {
  const [data, setData] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [analyticsRes, logRes] = await Promise.all([
          api.get(`/api/analytics/tickets?days=${days}`),
          api.get('/api/activity-log?limit=30'),
        ]);
        setData(analyticsRes.data);
        setActivityLog(logRes.data.logs || []);
      } catch (e) { /* handled */ }
      setLoading(false);
    })();
  }, [days]);

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!data) return <p className="text-center text-gray-400 py-12">No analytics data available</p>;

  const fmtMin = (m) => m >= 60 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)}m`;
  const maxDaily = Math.max(...(data.daily_trend || []).map(d => d.created), 1);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Ticket trends, response times, and platform activity</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32 rounded-lg" data-testid="analytics-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-stagger">
        <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
          <BarChart3 className="w-5 h-5 text-[#D4A843] mb-2" />
          <p className="font-heading text-2xl font-bold text-gray-900">{data.total}</p>
          <p className="text-xs text-gray-500">Total Tickets</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 hover-lift">
          <AlertTriangle className="w-5 h-5 text-amber-600 mb-2" />
          <p className="font-heading text-2xl font-bold text-amber-700">{data.open}</p>
          <p className="text-xs text-amber-600">Open</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 hover-lift">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="font-heading text-2xl font-bold text-emerald-700">{data.resolved}</p>
          <p className="text-xs text-emerald-600">Resolved</p>
        </div>
        <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5 hover-lift">
          <Clock className="w-5 h-5 text-[#D4A843] mb-2" />
          <p className="font-heading text-2xl font-bold text-[#D4A843]">{fmtMin(data.avg_resolution_min)}</p>
          <p className="text-xs text-[#D4A843]">Avg Resolution</p>
        </div>
      </div>

      {/* Ticket Trend Chart */}
      {(data.daily_trend || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#D4A843]" /> Ticket Volume Trend
          </h3>
          <div className="flex items-end gap-1 h-40" data-testid="ticket-trend-chart">
            {data.daily_trend.map((d, i) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-0.5" data-testid={`trend-bar-${i}`}>
                <div className="w-full flex flex-col gap-0.5">
                  <div className="bg-[#D4A843] rounded-t transition-all" style={{ height: `${(d.created / maxDaily) * 120}px` }} title={`${d.created} created`} />
                  {d.resolved > 0 && <div className="bg-emerald-400 rounded-b transition-all" style={{ height: `${(d.resolved / maxDaily) * 120}px` }} title={`${d.resolved} resolved`} />}
                </div>
                {i % Math.max(1, Math.floor(data.daily_trend.length / 7)) === 0 && (
                  <span className="text-[8px] text-gray-400 mt-1 -rotate-45 origin-top-left whitespace-nowrap">{d.date.slice(5)}</span>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-[#D4A843]" /> Created</span>
            <span className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded bg-emerald-400" /> Resolved</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Distribution */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">By Priority</h3>
          <div className="space-y-3">
            {['critical', 'high', 'medium', 'low'].map(p => {
              const count = data.priority_counts?.[p] || 0;
              const pct = data.total ? (count / data.total * 100) : 0;
              const colors = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-amber-400', low: 'bg-blue-400' };
              return (
                <div key={p} className="flex items-center gap-3" data-testid={`priority-${p}`}>
                  <span className="text-xs font-semibold text-gray-600 w-16 capitalize">{p}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${colors[p]} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">By Status</h3>
          <div className="space-y-3">
            {Object.entries(data.status_counts || {}).sort((a, b) => b[1] - a[1]).map(([s, count]) => {
              const pct = data.total ? (count / data.total * 100) : 0;
              const colors = { new: 'bg-blue-500', assigned: 'bg-indigo-500', in_progress: 'bg-amber-500', resolved: 'bg-emerald-500', closed: 'bg-gray-400' };
              return (
                <div key={s} className="flex items-center gap-3" data-testid={`status-${s}`}>
                  <span className="text-xs font-semibold text-gray-600 w-20 capitalize">{s.replace('_', ' ')}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${colors[s] || 'bg-gray-400'} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-bold text-gray-700 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Categories */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Top Categories</h3>
          <div className="space-y-2">
            {(data.category_counts || []).slice(0, 8).map((c, i) => (
              <div key={c.category} className="flex items-center justify-between p-2 rounded bg-gray-50" data-testid={`cat-${i}`}>
                <span className="text-xs text-gray-700 capitalize">{c.category.replace(/_/g, ' ')}</span>
                <span className="text-xs font-bold text-[#D4A843]">{c.count}</span>
              </div>
            ))}
            {(data.category_counts || []).length === 0 && <p className="text-xs text-gray-400 text-center py-4">No data</p>}
          </div>
        </div>

        {/* Industry Breakdown */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">By Industry</h3>
          <div className="space-y-2">
            {Object.entries(data.industry_counts || {}).sort((a, b) => b[1] - a[1]).map(([ind, count]) => (
              <div key={ind} className="flex items-center justify-between p-2 rounded bg-gray-50" data-testid={`ind-${ind}`}>
                <span className="text-xs text-gray-700 capitalize">{ind.replace(/_/g, ' ')}</span>
                <span className="text-xs font-bold text-[#D4A843]">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity Log */}
      {activityLog.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#D4A843]" /> Recent Activity
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {activityLog.map((log, i) => (
              <div key={`item-${i}`} className="flex items-center gap-3 p-2 rounded bg-gray-50 text-xs">
                <span className="text-gray-400 font-mono w-32 shrink-0">{new Date(log.created_at || log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-gray-600">{log.actor_name || 'System'}</span>
                <span className="text-gray-900 font-medium">{log.action}</span>
                <span className="text-gray-400 truncate">{typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details || '')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
